import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectClient } from '@client/model';
import { useToast } from '@client/components/hooks/use-toast';
import { anonymousPuiApi } from '@client/api/waterlily';

export function useAnonymousPui() {
  const client = useSelector(selectClient);
  const clientId = client?.clientId;
  const { toast } = useToast();
  const [puiSentData, setPuiSentData] = useState<PUIMetadata[] | null>(null);

  const mutation = useMutation({
    mutationFn: (options: SendAnonymousPuiOptions) => {
      if (!clientId) {
        throw new Error('Client ID is required');
      }
      return anonymousPuiApi.sendRequest(clientId, options);
    },
    onSuccess: data => {
      setPuiSentData(data.clientMetadataUpdates);
      const successDescription =
        data.emailsSent.length > 0
          ? `Sent to ${data.emailsSent.length} carrier${data.emailsSent.length === 1 ? '' : 's'}: ${data.emailsSent.join(', ')}.`
          : 'No emails were sent.';
      const failedDescription =
        data.emailsFailed.length > 0
          ? ` Failed to send to ${data.emailsFailed.length} carrier${data.emailsFailed.length === 1 ? '' : 's'}: ${data.emailsFailed.join(', ')}.`
          : '';
      toast({
        title: 'Anonymous PUI request sent successfully',
        description: `${successDescription}${failedDescription}`,
        variant: 'default',
        duration: 10000,
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message;

      toast({
        title: 'Failed to send anonymous PUI request',
        description: message || 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
        duration: 10000,
      });
    },
  });

  return {
    ...mutation,
    puiSentData,
  };
}
